import { ChangeDetectionStrategy, Component, computed, inject, signal, type OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { type AssistantSettings, type ServiceDeskMember } from '@/cloud/models/assistant.models';
import { BreadcrumbsComponent, type BreadcrumbItem } from '@/shared/breadcrumbs/breadcrumbs.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { AssistantSettingsCardComponent } from '@/cloud/components/assistant/assistant-settings-card.component';
import { ServiceDeskGrantComponent } from '@/cloud/components/assistant/service-desk-grant.component';

interface UserOption {
    id: string;
    label: string;
    /** Tenant ids the user is a member of. */
    memberOf?: string[];
}

/**
 * Superadmin page for the service desk: who can ask the assistant across tenants (their tenants, whether
 * they may propose actions, expiry), adding and editing members, and the platform Anthropic key the mode runs on.
 */
@Component({
    selector: 'app-service-desk',
    standalone: true,
    imports: [
        DatePipe,
        FormsModule,
        ButtonModule,
        ConfirmDialogModule,
        DialogModule,
        InputTextModule,
        SelectModule,
        TableModule,
        TagModule,
        TooltipModule,
        BreadcrumbsComponent,
        EmptyStateComponent,
        AssistantSettingsCardComponent,
        ServiceDeskGrantComponent
    ],
    providers: [ConfirmationService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: '../platform-keys/platform-keys.scss',
    template: `
        <p-confirmdialog />
        <div class="management-page">
            <app-breadcrumbs [model]="breadcrumbs" />
            <header class="overview-header">
                <div>
                    <p class="overview-eyebrow">Administration</p>
                    <h1>Service desk</h1>
                    <p class="overview-description">People who can ask the assistant about all their tenants at once, and the platform key it runs on. Access only covers tenants each person is a member of.</p>
                </div>
                <div class="overview-actions">
                    <p-button label="Assistant key" icon="pi pi-cog" [outlined]="true" (onClick)="keyDialog.set(true)" />
                    <p-button label="Add member" icon="pi pi-user-plus" (onClick)="openAdd()" />
                </div>
            </header>

            <div class="platform-assistant-bar">
                <i class="pi" [class.pi-check-circle]="keyReady()" [class.pi-exclamation-triangle]="!keyReady()" aria-hidden="true"></i>
                @if (keyReady()) {
                    <span><strong>Platform key active</strong> ({{ platformKey()?.keyHint }}, {{ platformKey()?.model }}). Service desk conversations are billed to it, never to a tenant.</span>
                } @else {
                    <span><strong>No platform key yet.</strong> Members can't use service desk mode until a superadmin adds one under Assistant key.</span>
                }
            </div>

            <section class="management-panel" aria-label="Service desk members">
                <div class="management-toolbar">
                    <label class="management-search">
                        <i class="pi pi-search" aria-hidden="true"></i>
                        <input pInputText type="search" aria-label="Search members" placeholder="Search members or tenants" [ngModel]="search()" (ngModelChange)="search.set($event || '')" />
                    </label>
                    <span class="management-results">{{ activeCount() }} active · {{ members().length }} total</span>
                </div>
                <p-table class="management-table" [value]="filtered()" [loading]="loading()" [scrollable]="true" dataKey="userId">
                    <ng-template #header>
                        <tr>
                            <th>Member</th>
                            <th>Tenants</th>
                            <th class="hidden md:table-cell">Actions</th>
                            <th class="hidden md:table-cell">Expires</th>
                            <th class="hidden lg:table-cell">Granted</th>
                            <th>Status</th>
                            <th><span class="sr-only">Manage</span></th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-m>
                        <tr>
                            <td>
                                <div class="font-medium min-w-40">{{ m.displayName || m.email || m.userId }}</div>
                                @if (m.displayName && m.email) {
                                    <div class="text-sm text-muted-color">{{ m.email }}</div>
                                }
                            </td>
                            <td class="text-sm">
                                <div class="min-w-48">{{ tenantText(m) }}</div>
                            </td>
                            <td class="hidden md:table-cell">
                                @if (m.superAdmin || m.grant?.canProposeActions) {
                                    <p-tag value="May propose" severity="info" [rounded]="true" pTooltip="Can have the assistant propose reboots and restarts, which they confirm" />
                                } @else {
                                    <span class="text-sm text-muted-color">Read only</span>
                                }
                            </td>
                            <td class="hidden md:table-cell text-sm">{{ m.superAdmin ? '—' : m.grant?.expiresUtc ? (m.grant.expiresUtc | date: 'mediumDate') : 'Never' }}</td>
                            <td class="hidden lg:table-cell text-sm">
                                @if (m.superAdmin) {
                                    <span class="text-muted-color">Implicit (superadmin)</span>
                                } @else {
                                    {{ m.grant?.grantedUtc | date: 'mediumDate' }}{{ m.grantedByName ? ' by ' + m.grantedByName : '' }}
                                }
                            </td>
                            <td><p-tag [value]="statusLabel(m)" [severity]="statusSeverity(m)" [rounded]="true" /></td>
                            <td>
                                <div class="management-row-actions">
                                    @if (!m.superAdmin) {
                                        <p-button label="Edit" [text]="true" size="small" [ariaLabel]="'Edit service desk access for ' + (m.displayName || m.email)" (onClick)="edit(m)" />
                                        <p-button label="Revoke" severity="danger" [text]="true" size="small" [ariaLabel]="'Revoke service desk access for ' + (m.displayName || m.email)" (onClick)="revoke(m)" />
                                    }
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template #emptymessage>
                        <tr>
                            <td colspan="7">
                                <app-empty-state icon="pi pi-shield" [title]="search() ? 'No members match' : 'No service desk members yet'" />
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </section>
        </div>

        <p-dialog [header]="editing() ? 'Service desk access — ' + editing()!.label : 'Add a service desk member'" [visible]="memberDialog()" (visibleChange)="closeMember($event)" [modal]="true" [style]="{ width: '40rem', maxWidth: '95vw' }" appendTo="body" [draggable]="false">
            @if (memberDialog()) {
                @if (!editing()) {
                    <label for="sd-user" class="block mb-2 font-medium">Person</label>
                    <p-select inputId="sd-user" [options]="userOptions()" optionLabel="label" optionValue="id" [filter]="true" filterBy="label" placeholder="Choose a user" class="w-full mb-4" appendTo="body" [ngModel]="pickedUser()" (ngModelChange)="pickUser($event)" />
                }
                @if (editing(); as e) {
                    <app-service-desk-grant [userId]="e.id" [namespaces]="tenantOptions()" [memberOf]="editingMemberOf()" (changed)="onChanged()" />
                }
            }
        </p-dialog>

        <p-dialog header="Service desk assistant key" [visible]="keyDialog()" (visibleChange)="closeKey($event)" [modal]="true" [style]="{ width: '44rem', maxWidth: '95vw' }" appendTo="body" [draggable]="false">
            @if (keyDialog()) {
                <app-assistant-settings-card scope="platform" [canManage]="true" />
            }
        </p-dialog>
    `
})
export class ServiceDesk implements OnInit {
    private api = inject(CloudApiService);
    private confirm = inject(ConfirmationService);
    private messages = inject(MessageService);

    readonly breadcrumbs: BreadcrumbItem[] = [
        { label: 'Dashboard', routerLink: '/dashboard' },
        { label: 'Service desk', routerLink: '/admin/service-desk' }
    ];

    readonly members = signal<ServiceDeskMember[]>([]);
    readonly loading = signal(false);
    readonly search = signal('');
    readonly platformKey = signal<AssistantSettings | null>(null);
    readonly keyDialog = signal(false);
    readonly memberDialog = signal(false);
    readonly editing = signal<UserOption | null>(null);
    readonly pickedUser = signal<string | null>(null);
    private readonly users = signal<UserOption[]>([]);

    readonly keyReady = computed(() => !!this.platformKey()?.enabled);
    readonly activeCount = computed(() => this.members().filter((m) => m.status === 'active' || m.status === 'superadmin').length);
    readonly filtered = computed(() => {
        const terms = this.search().toLowerCase().split(/\s+/).filter(Boolean);
        if (terms.length === 0) return this.members();
        return this.members().filter((m) => {
            const haystack = [m.displayName, m.email, this.tenantText(m)].join(' ').toLowerCase();
            return terms.every((t) => haystack.includes(t));
        });
    });
    readonly tenantOptions = computed(() =>
        (this.api.namespaces() ?? []).map((n) => ({ id: n.id ?? n.Id, name: n.name ?? n.Name ?? n.id })).sort((a, b) => String(a.name).localeCompare(String(b.name)))
    );
    /** Tenants the person being edited belongs to; null until the user list has loaded. */
    readonly editingMemberOf = computed(() => this.users().find((u) => u.id === this.editing()?.id)?.memberOf ?? null);
    /** People who aren't members yet; superadmins already have access. */
    readonly userOptions = computed(() => {
        const members = new Set(this.members().map((m) => m.userId));
        return this.users().filter((u) => !members.has(u.id));
    });

    ngOnInit(): void {
        this.load();
        this.loadKey();
        this.api.getNamespaces().catch(() => undefined);
        this.api
            .getUsers()
            .then((list: { id: string; displayName?: string; username?: string; email?: string; superAdmin?: boolean; permissions?: { appNamespaceId?: string }[] }[]) =>
                this.users.set(
                    list
                        .filter((u) => !u.superAdmin)
                        .map((u) => ({
                            id: u.id,
                            label: [u.displayName || u.username, u.email].filter(Boolean).join(' — ') || u.id,
                            memberOf: (u.permissions ?? []).map((p) => p.appNamespaceId).filter((id): id is string => !!id)
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                )
            )
            .catch(() => undefined);
    }

    load(): void {
        this.loading.set(true);
        this.api
            .getServiceDeskMembers()
            .then((m) => this.members.set(m))
            .catch(() => this.messages.add({ severity: 'error', summary: 'Could not load service desk members' }))
            .finally(() => this.loading.set(false));
    }

    loadKey(): void {
        this.api
            .getPlatformAssistantSettings()
            .then((s) => this.platformKey.set(s))
            .catch(() => this.platformKey.set(null));
    }

    tenantText(m: ServiceDeskMember): string {
        if (m.superAdmin) return 'All tenants';
        if (m.grant?.allNamespaces) return 'All their tenants';
        return m.tenants.map((t) => t.name).join(', ') || '—';
    }

    statusLabel(m: ServiceDeskMember): string {
        return { active: 'Active', expired: 'Expired', superadmin: 'Superadmin', disabled: 'Disabled' }[m.status];
    }

    statusSeverity(m: ServiceDeskMember): 'success' | 'secondary' | 'info' | 'warn' {
        return m.status === 'active' ? 'success' : m.status === 'superadmin' ? 'info' : m.status === 'expired' ? 'warn' : 'secondary';
    }

    openAdd(): void {
        this.editing.set(null);
        this.pickedUser.set(null);
        this.memberDialog.set(true);
    }

    pickUser(id: string | null): void {
        this.pickedUser.set(id);
        const user = this.users().find((u) => u.id === id);
        this.editing.set(user ?? null);
    }

    edit(m: ServiceDeskMember): void {
        this.editing.set({ id: m.userId, label: m.displayName || m.email || m.userId });
        this.memberDialog.set(true);
    }

    closeMember(visible: boolean): void {
        this.memberDialog.set(visible);
        if (!visible) this.editing.set(null);
    }

    closeKey(visible: boolean): void {
        this.keyDialog.set(visible);
        if (!visible) this.loadKey();
    }

    onChanged(): void {
        this.load();
    }

    revoke(m: ServiceDeskMember): void {
        const name = m.displayName || m.email || m.userId;
        this.confirm.confirm({
            header: 'Revoke service desk access',
            message: `${name} will no longer be able to ask the assistant across tenants. Takes effect on their next message.`,
            acceptLabel: 'Revoke',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () =>
                this.api
                    .removeServiceDeskGrant(m.userId)
                    .then(() => {
                        this.messages.add({ severity: 'success', summary: `Revoked service desk access for ${name}` });
                        this.load();
                    })
                    .catch(() => this.messages.add({ severity: 'error', summary: 'Could not revoke access' }))
        });
    }
}
